import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import MypageMenu from '../../common/MypageMenu';
import '../../common/Mypage.scss';
import { userDataAtom } from '../../../../AtomStore';
import { fetchScrapList, type ScrapListItem } from '../../common/scrapApi';

type FilterType = 'all' | 'recruit' | 'retreat_place' | 'retreat_casting';

const FILTERS: { id: FilterType; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'recruit', label: '구인구직' },
  { id: 'retreat_place', label: '수련회 장소' },
  { id: 'retreat_casting', label: '수련회 강사' },
];

function scrapTypeLabel(targetType: string) {
  if (targetType === 'recruit') return '구인구직';
  if (targetType === 'retreat_place') return '수련회 장소';
  if (targetType === 'retreat_casting') return '수련회 강사';
  return targetType;
}

function scrapTagClass(targetType: string) {
  if (targetType === 'recruit') return 'mypage-tag--green';
  return 'mypage-tag--gold';
}

function formatScrapDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '-', time: '' };
  return {
    date: d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' }),
    time: d.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' }),
  };
}

function fallbackPath(item: ScrapListItem) {
  if (item.targetType === 'recruit') {
    if (item.tableType === 'church') return `/recruit/church/${item.targetId}`;
    if (item.tableType === 'institute') return `/recruit/institute/${item.targetId}`;
    if (item.tableType === 'overseas') return `/recruit/overseas/${item.targetId}`;
    return `/recruit/minister/${item.targetId}`;
  }
  if (item.targetType === 'retreat_place') {
    return `/retreat/place/detail?id=${item.targetId}`;
  }
  return `/retreat/casting/detail?id=${item.targetId}`;
}

export default function ScrapManage() {
  const navigate = useNavigate();
  const [userData] = useAtom(userDataAtom);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [list, setList] = useState<ScrapListItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchScrapList(userData.userAccount || '');
        if (!cancelled) setList(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData.userAccount]);

  const filteredList = useMemo(() => {
    if (filter === 'all') return list;
    return list.filter(item => item.targetType === filter);
  }, [filter, list]);

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            <div className="mypage-section-head">
              <div>
                <h2 className="mypage-section-title">스크랩 관리</h2>
                <p className="mypage-section-copy">저장한 스크랩을 유형별로 확인하고 바로 이동할 수 있습니다.</p>
              </div>
              <div className="mypage-filter-row">
                {FILTERS.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`mypage-filter-chip ${filter === tab.id ? 'is-active' : ''}`}
                    onClick={() => setFilter(tab.id)}>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="noPosts">
                <p>불러오는 중입니다...</p>
              </div>
            ) : filteredList.length > 0 ? (
              <div className="mypage-scrap-list">
                {filteredList.map(item => {
                  const { date, time } = formatScrapDate(item.createdAt);
                  return (
                    <article
                      key={item.id}
                      className="mypage-scrap-item"
                      onClick={() => {
                        navigate(item.linkPath || fallbackPath(item));
                        window.scrollTo(0, 0);
                      }}>
                      <div className="mypage-row-between">
                        <div className="mypage-tag-row">
                          <span className="mypage-tag mypage-tag--sky">{item.targetType}</span>
                          <span className={`mypage-tag ${scrapTagClass(item.targetType)}`}>
                            {scrapTypeLabel(item.targetType)}
                          </span>
                        </div>
                        <span className="mypage-meta">
                          <span>{date}</span>
                          {time ? <span>{time}</span> : null}
                        </span>
                      </div>
                      <h4 className="mypage-scrap-title">{item.title || '(제목 없음)'}</h4>
                      <div className="mypage-meta">
                        {item.subtitle ? <span><strong>부제</strong> {item.subtitle}</span> : null}
                        {item.meta ? <span><strong>정보</strong> {item.meta}</span> : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="noPosts">
                <p>스크랩한 항목이 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
